import { Clickable, Icon } from 'components';
import { theme } from 'lib';
import React, { useEffect, useRef } from 'react';
import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideUp = keyframes`
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: ${fadeIn} 0.2s ease-out;
  padding: 20px;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 16px;
  width: ${(props) => props.width || '800px'};
  max-width: 95vw;
  max-height: 90vh;
  position: relative;
  display: flex;
  flex-direction: column;
  animation: ${slideUp} 0.3s ease-out;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
`;

const Header = styled.div`
  flex-shrink: 0;
  padding: 24px 24px 16px 24px;
  border-bottom: ${(props) => (props.noBorder ? 'none' : `1px solid ${theme.GREY_20}`)};
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: transparent;
  border: none;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.GREY_10};
    transform: scale(1.1);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const Content = styled.div`
  overflow-y: ${(props) => (props.overflowY ? props.overflowY : 'auto')};
  overflow-x: ${(props) => (props.overflowX ? props.overflowX : 'hidden')};
  padding: 24px;
  flex: 1;

  /* Custom scrollbar */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.GREY_10};
    border-radius: 4px;
    margin: 4px 0;
  }

  &::-webkit-scrollbar-thumb {
    background: ${theme.GREY_40};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${theme.GREY_60};
  }
`;

const Footer = styled.div`
  flex-shrink: 0;
  padding: 16px 24px 24px 24px;
  border-top: ${(props) => (props.noBorder ? 'none' : `1px solid ${theme.GREY_20}`)};
  background: ${(props) => props.background || 'white'};
`;

const ModalV2 = ({
  visible,
  closeModal,
  children,
  width,
  onOverlayClick,
  header,
  footer,
  hideCloseButton = false,
  noBorderHeader = false,
  noBorderFooter = false,
  footerBackground,
  overflowY,
  overflowX,
  /** Accessible name, for when `header` is markup rather than a string. */
  ariaLabel
}) => {
  const containerRef = useRef(null);
  const returnFocusTo = useRef(null);

  /*
    Escape, scroll lock, and — added later — focus.

    The dialog already trapped nothing: opening it left focus behind on the page underneath, Tab
    walked out of the modal into content the overlay was covering, and dismissing it dropped focus
    on `<body>`. For a confirmation dialog that is not a nicety: the one control that matters is
    the one a keyboard user could not reach.

    Added here rather than in a caller so every modal in the CMS gains it.
  */
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && visible) {
        closeModal();
      }
    };

    /** Keeps Tab and Shift+Tab inside the dialog while it is open. */
    const handleTab = (e) => {
      if (e.key !== 'Tab' || !visible || !containerRef.current) return;

      const focusable = containerRef.current.querySelectorAll(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    };

    if (visible) {
      returnFocusTo.current = document.activeElement;
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleTab);
      document.body.style.overflow = 'hidden';

      // Into the dialog, not merely near it. The first focusable control if there is one, the
      // container otherwise — a dialog that is only text still needs to be where the reader is.
      const target =
        containerRef.current?.querySelector(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) ?? containerRef.current;
      target?.focus?.();
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTab);
      document.body.style.overflow = 'unset';

      // Back to whatever opened it, so dismissing does not strand focus on <body>.
      if (returnFocusTo.current?.focus) returnFocusTo.current.focus();
    };
  }, [visible, closeModal]);

  if (!visible) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (onOverlayClick) {
        onOverlayClick();
      } else {
        closeModal();
      }
    }
  };

  return (
    <Overlay onClick={handleOverlayClick}>
      <ModalContainer
        width={width}
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof header === 'string' ? header : ariaLabel}
        tabIndex={-1}
      >
        {header && (
          <Header noBorder={noBorderHeader}>
            {!hideCloseButton && (
              <CloseButton onClick={closeModal} type="button">
                <Icon type="close" dimensions={14} col={theme.GREY_80} />
              </CloseButton>
            )}
            {header}
          </Header>
        )}

        {!header && !hideCloseButton && (
          <CloseButton onClick={closeModal} type="button" style={{ position: 'absolute' }}>
            <Icon type="close" dimensions={14} col={theme.GREY_80} />
          </CloseButton>
        )}

        <Content overflowY={overflowY} overflowX={overflowX}>
          {children}
        </Content>

        {footer && (
          <Footer noBorder={noBorderFooter} background={footerBackground}>
            {footer}
          </Footer>
        )}
      </ModalContainer>
    </Overlay>
  );
};

export default ModalV2;
